import axios from "axios";
import { serverLink } from "./serverLink";

const axiosInstance = axios.create({
  baseURL: `${serverLink}/api/`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Track in-flight refresh to avoid duplicate calls
let refreshPromise = null;

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    } else {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      error.response?.data?.code === "TOKEN_EXPIRED" &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) {
        forceLogout();
        return Promise.reject(error);
      }

      try {
        // Reuse in-flight refresh if one is already happening
        if (!refreshPromise) {
          refreshPromise = axios.post(`${serverLink}/api/auth/refresh-token`, {
            refreshToken,
          });
        }

        const { data } = await refreshPromise;
        refreshPromise = null;

        const newAccessToken = data.data.accessToken;
        const newRefreshToken = data.data.refreshToken;

        localStorage.setItem("authToken", newAccessToken);
        localStorage.setItem("refreshToken", newRefreshToken);

        // Lazy require avoids circular init: store → rootReducer → userSlice → userAPI → axiosInstance
        const { sessionTokensUpdated } = require("../features/user/userSlice");
        const store = require("../store/store").default;
        store.dispatch(
          sessionTokensUpdated({
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
          }),
        );

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch {
        refreshPromise = null;
        forceLogout();
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

function forceLogout() {
  localStorage.removeItem("authToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("adminId");
  localStorage.removeItem("adminName");
  window.location.href = "/login";
}

export default axiosInstance;
